import * as React from "react";
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { TowingEquipmentAdmin } from "../../../../hooks/useTowingEquipmentAdmin";
import { scrollSx } from "../../../../utils";

interface Props {
  equipment: TowingEquipmentAdmin[];
  selectedEquipment: TowingEquipmentAdmin | null;
  onSelectEquipment: (equipment: TowingEquipmentAdmin, index: number) => void;
  onEditClick: (equipment: TowingEquipmentAdmin, event: React.MouseEvent) => void;
  onDeleteClick: (equipment: TowingEquipmentAdmin, event: React.MouseEvent) => void;
  tableRef: React.RefObject<HTMLDivElement>;
}

export const EquipmentTable: React.FC<Props> = ({
  equipment,
  selectedEquipment,
  onSelectEquipment,
  onEditClick,
  onDeleteClick,
  tableRef,
}) => {
  const { t } = useTranslation();
  return (
    <TableContainer
      ref={tableRef}
      component={Paper}
      sx={{ flex: 1, overflow: 'auto', maxHeight: '100%', ...scrollSx }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '15%', backgroundColor: 'background.default' }}>
              {t("components.equipmentTable.nameHeader")}
            </TableCell>
            <TableCell align="right" sx={{ width: '10%', backgroundColor: 'background.default' }}>
              {t("components.equipmentTable.widthHeader")}
            </TableCell>
            <TableCell align="right" sx={{ width: '10%', backgroundColor: 'background.default' }}>
              {t("components.equipmentTable.lengthHeader")}
            </TableCell>
            <TableCell align="right" sx={{ width: '10%', backgroundColor: 'background.default' }}>
              {t("components.equipmentTable.heightHeader")}
            </TableCell>
            <TableCell align="right" sx={{ width: '10%', backgroundColor: 'background.default' }}>
              {t("components.equipmentTable.towBarHeader")}
            </TableCell>
            <TableCell align="center" sx={{ width: '10%', backgroundColor: 'background.default' }}>
              {t("components.equipmentTable.actionsHeader")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {equipment.map((equip, index) => (
            <TableRow
              key={equip.id}
              hover
              selected={selectedEquipment?.id === equip.id}
              onClick={() => onSelectEquipment(equip, index)}
              sx={{ cursor: 'pointer', height: '48px' }}
            >
              <TableCell>{equip.name}</TableCell>
              <TableCell align="right">{equip.width.toFixed(2)}</TableCell>
              <TableCell align="right">{equip.length.toFixed(2)}</TableCell>
              <TableCell align="right">{equip.height.toFixed(2)}</TableCell>
              <TableCell align="right">
                {equip.tow_bar_length !== null && equip.tow_bar_length !== undefined
                  ? equip.tow_bar_length.toFixed(2)
                  : "—"}
              </TableCell>
              <TableCell align="center">
                <Stack direction="row" spacing={1} justifyContent="center">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={(e) => onEditClick(equip, e)}
                    sx={{ minWidth: 'auto' }}
                  >
                    {t("components.equipmentTable.editButton")}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={(e) => onDeleteClick(equip, e)}
                    sx={{ minWidth: 'auto' }}
                  >
                    {t("components.equipmentTable.deleteButton")}
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
